import type { Tenant, Branch, CardColor, AvatarVariant, CardRequest, AttemptCounter, Orientation, AvatarStyleOption } from "./types";

import styleBitmoji from "@/assets/style-bitmoji.jpg";
import styleSportstar from "@/assets/style-sportstar.jpg";
import stylePopstar from "@/assets/style-popstar.jpg";
import styleSuperhero from "@/assets/style-superhero.jpg";
import styleCartoon from "@/assets/style-cartoon.jpg";
import styleWithCat from "@/assets/style-with-cat.jpg";
import styleWithDog from "@/assets/style-with-dog.jpg";
import styleCartoonCharacter from "@/assets/style-cartoon-character.jpg";
import styleSuperheroComic from "@/assets/style-superhero-comic.jpg";
import styleAnimeModern from "@/assets/style-anime-modern.jpg";
import stylePopArt from "@/assets/style-pop-art.jpg";
import styleFuturisticHologram from "@/assets/style-futuristic-hologram.jpg";
import styleFantasyCinematic from "@/assets/style-fantasy-cinematic.jpg";
import styleMinimalLineArt from "@/assets/style-minimal-line-art.jpg";
import styleSciFiMecha from "@/assets/style-sci-fi-mecha.jpg";
import styleDisneyInspired from "@/assets/style-disney-inspired.jpg";
import styleSuperMarioInspired from "@/assets/style-super-mario-inspired.jpg";
import styleMarvelInspired from "@/assets/style-marvel-inspired.jpg";

// ── Shared palette pools (tenants pick from these) ──────────────────
export const ALL_CARD_COLORS: CardColor[] = [
  { id: "midnight", name: "Midnight", hsl: "230 25% 15%" },
  { id: "ocean", name: "Ocean", hsl: "210 80% 50%" },
  { id: "sunset", name: "Sunset", hsl: "15 85% 55%" },
  { id: "forest", name: "Forest", hsl: "150 60% 35%" },
  { id: "plum", name: "Plum", hsl: "280 55% 45%" },
  { id: "slate", name: "Slate", hsl: "215 15% 50%" },
  { id: "gold", name: "Gold", hsl: "42 85% 55%" },
  { id: "rose", name: "Rose", hsl: "340 70% 60%" },
  { id: "blue", name: "Blue", hsl: "220 90% 56%" },
  { id: "red", name: "Red", hsl: "0 85% 50%" },
  { id: "green", name: "Green", hsl: "130 70% 40%" },
  { id: "snap-yellow", name: "Snap Yellow", hsl: "50 100% 50%" },
];

export const ALL_AVATAR_STYLES: AvatarStyleOption[] = [
  {
    id: "cartoon_character",
    label: "Cartoon",
    description: "Semi-realistic cartoon character with expressive face and smooth 3D lighting",
    emoji: "🎨",
    templateImage: styleCartoonCharacter,
  },
  {
    id: "superhero_comic",
    label: "Super Hero",
    description: "Classic comic-book hero with dynamic pose and dramatic lighting",
    emoji: "🦸",
    templateImage: styleSuperheroComic,
  },
  {
    id: "anime_modern",
    label: "Anime",
    description: "Stylized 2D Japanese anime aesthetic with clean linework and expressive eyes",
    emoji: "✨",
    templateImage: styleAnimeModern,
  },
  {
    id: "pop_art",
    label: "Pop Art",
    description: "1960s-inspired bold outlines, halftone dots, and saturated colors",
    emoji: "🎪",
    templateImage: stylePopArt,
  },
  {
    id: "futuristic_hologram",
    label: "Futuristic Hologram",
    description: "Translucent glowing portrait with neon blue tones and particle effects",
    emoji: "🔮",
    templateImage: styleFuturisticHologram,
  },
  {
    id: "fantasy_cinematic",
    label: "Fantasy",
    description: "Realistic fantasy character with cinematic lighting and epic atmosphere",
    emoji: "⚔️",
    templateImage: styleFantasyCinematic,
  },
  {
    id: "minimal_line_art",
    label: "Minimal Line Art",
    description: "Simplified continuous line drawing with abstract shapes",
    emoji: "✏️",
    templateImage: styleMinimalLineArt,
  },
  {
    id: "sci_fi_mecha",
    label: "Sci-Fi",
    description: "Futuristic style with robotic, metallic, and exosuit design elements",
    emoji: "🤖",
    templateImage: styleSciFiMecha,
  },
  {
    id: "flat_avatar_with_pet_cat",
    label: "With Cat",
    description: "Flat vector-style avatar with your favourite feline friend",
    emoji: "🐱",
    templateImage: styleWithCat,
    subChoices: [
      { id: "tabby", label: "Tabby", emoji: "🐈" },
      { id: "siamese", label: "Siamese", emoji: "🐱" },
      { id: "persian", label: "Persian", emoji: "😸" },
      { id: "black_cat", label: "Black Cat", emoji: "🐈‍⬛" },
      { id: "ginger", label: "Ginger", emoji: "🧡" },
    ],
  },
  {
    id: "flat_avatar_with_dog",
    label: "With Dog",
    description: "Flat vector avatar with a loyal canine companion",
    emoji: "🐶",
    templateImage: styleWithDog,
    subChoices: [
      { id: "labrador", label: "Labrador", emoji: "🦮" },
      { id: "poodle", label: "Poodle", emoji: "🐩" },
      { id: "bulldog", label: "Bulldog", emoji: "🐶" },
      { id: "husky", label: "Husky", emoji: "🐺" },
      { id: "corgi", label: "Corgi", emoji: "🐕" },
    ],
  },
  {
    id: "disney_inspired",
    label: "Disney Inspired",
    description: "Warm, stylized character design with expressive eyes and magical tone",
    emoji: "🏰",
    templateImage: styleDisneyInspired,
  },
  {
    id: "super_mario_inspired",
    label: "Mario Inspired",
    description: "Geometric cartoon style with clean vector forms and playful shapes",
    emoji: "🍄",
    templateImage: styleSuperMarioInspired,
  },
  {
    id: "marvel_inspired",
    label: "Marvel Inspired",
    description: "Dynamic comic-book realism with cinematic framing and bold contrast",
    emoji: "💥",
    templateImage: styleMarvelInspired,
  },
  {
    id: "bitmoji_inspired",
    label: "Bitmoji",
    description: "Simplified flat cartoon avatar with expressive, friendly design",
    emoji: "😊",
    templateImage: styleBitmoji,
  },
  {
    id: "sports_3d_character",
    label: "Sports",
    description: "Stylized 3D character with athletic appearance and bright lighting",
    emoji: "🏆",
    templateImage: styleSportstar,
    subChoices: [
      { id: "football", label: "Football", emoji: "⚽" },
      { id: "basketball", label: "Basketball", emoji: "🏀" },
      { id: "tennis", label: "Tennis", emoji: "🎾" },
      { id: "swimming", label: "Swimming", emoji: "🏊" },
      { id: "cricket", label: "Cricket", emoji: "🏏" },
      { id: "rugby", label: "Rugby", emoji: "🏉" },
      { id: "athletics", label: "Athletics", emoji: "🏃" },
    ],
  },
  {
    id: "pop_star",
    label: "Pop Star",
    description: "Dynamic expressive illustration with retro-pop aesthetic and stage charisma",
    emoji: "🎤",
    templateImage: stylePopstar,
  },
];

export const ALL_HOBBIES: { id: string; label: string; emoji: string }[] = [
  { id: "gaming", label: "Gaming", emoji: "🎮" },
  { id: "music", label: "Music", emoji: "🎵" },
  { id: "sports", label: "Sports", emoji: "⚽" },
  { id: "cooking", label: "Cooking", emoji: "🍳" },
  { id: "travel", label: "Travel", emoji: "✈️" },
  { id: "art", label: "Art", emoji: "🎨" },
  { id: "reading", label: "Reading", emoji: "📚" },
  { id: "photography", label: "Photography", emoji: "📷" },
  { id: "fitness", label: "Fitness", emoji: "💪" },
  { id: "gardening", label: "Gardening", emoji: "🌱" },
];

// ── Tenants ─────────────────────────────────────────────────────────
export const MOCK_TENANTS: Record<string, Tenant> = {
  "acme-bank": {
    id: "t1",
    slug: "acme-bank",
    name: "Acme Bank",
    brandName: "Acme Bank",
    tenantType: "bank",
    primaryColor: "220 75% 45%",
    logoUrl: "",
    freeAttemptsPerUser: 5,
    overagePrice: 2.99,
    billingMode: "bank_billed",
    notificationEmail: "cards@acmebank.com",
    landingPage: {
      heroTitle: "Your Card. Your Style. Your Avatar.",
      heroSubtitle: "Create a personalised Acme Bank card with an AI-generated avatar or your own photo. Stand out every time you pay.",
      features: [],
      ctaText: "Design Your Card",
      footerText: "Powered by Cardmoji",
    },
    policies: {
      privacyPolicy: `<h2>Privacy Policy — Acme Bank</h2>
<p><strong>Effective Date:</strong> 1 January 2026</p>
<p>Acme Bank ("we", "us", "our") is committed to protecting your privacy. This policy explains how we collect, use, and safeguard your personal information when you use our personalised card service.</p>
<h3>1. Information We Collect</h3>
<p>We collect the following data when you use this service:</p>
<ul><li>Your name and contact details</li><li>Photographs and selfies you upload</li><li>AI-generated avatar images</li><li>Card design preferences (colour, style, orientation)</li><li>Device and browser information for security purposes</li></ul>
<h3>2. How We Use Your Information</h3>
<ul><li>To generate and print your personalised bank card</li><li>To verify your identity and prevent fraud</li><li>To improve our AI avatar generation technology</li><li>To communicate with you about your card order</li></ul>
<h3>3. Data Sharing</h3>
<p>We do not sell your personal data. We may share information with: our card printing partners, AI processing services, and regulatory authorities where required by law.</p>
<h3>4. Data Retention</h3>
<p>Your uploaded photos are retained for 90 days after card production, or as configured by your bank's retention policy. AI-generated images are the property of Marketspringpad IP USA LLC (see Terms & Conditions) and are retained as needed. You may request deletion of your uploaded photos at any time.</p>
<h3>5. Your Rights</h3>
<p>You have the right to access, correct, delete, or port your data. Contact us at privacy@acmebank.com.</p>
<h3>6. Cookies</h3>
<p>We use essential cookies for session management. No third-party tracking cookies are used.</p>
<h3>7. Contact</h3>
<p>Data Protection Officer: privacy@acmebank.com</p>`,
      termsConditions: `<h2>Terms & Conditions — Acme Bank Card Personalisation</h2>
<p><strong>Effective Date:</strong> 1 January 2026</p>
<h3>1. Service Description</h3>
<p>Acme Bank provides a personalised card service allowing you to customise the appearance of your bank card using AI-generated avatars or personal photographs.</p>
<h3>2. Eligibility</h3>
<p>This service is available to Acme Bank account holders aged 18 and over with an active debit or credit card.</p>
<h3>3. Acceptable Use</h3>
<p>You agree not to upload images that are: offensive, obscene, or discriminatory; infringe on third-party intellectual property; depict violence or illegal activity; or impersonate another person.</p>
<h3>4. Content Review</h3>
<p>All card designs are subject to review by our team. We reserve the right to reject any design that does not meet our guidelines.</p>
<h3>5. Fees</h3>
<p>You are entitled to ${"{tenant.freeAttemptsPerUser}"} free avatar generation attempts. Additional attempts are charged at the prevailing rate. Card printing and delivery are free of charge.</p>
<h3>6. Intellectual Property</h3>
<p>All AI-generated images and artwork created through this service are the exclusive intellectual property of Marketspringpad IP USA LLC ("IP Owner"). The issuing bank is granted a limited, non-exclusive, non-transferable licence to use the generated images solely for the purposes of card production and display within its digital banking platforms (including mobile and online banking applications). You, the cardholder, receive no ownership rights in the generated images. You may not reproduce, distribute, sublicence, or commercially exploit the generated artwork. Uploaded selfies remain your property and are subject to the data retention policy.</p>
<h3>7. Liability</h3>
<p>Acme Bank is not liable for delays in card production or delivery caused by third-party services. Card designs are provided "as is".</p>
<h3>8. Governing Law</h3>
<p>These terms are governed by the laws of England and Wales.</p>`,
    },
    allowPhotoOnly: true,
    defaultFulfilment: "in_branch",
    printerWebhookUrl: "https://print.acmebank.com/webhook",
    cardLayout: {
      logoPosition: { x: 5, y: 5 },
      chipPosition: { x: 8, y: 45 },
      avatarZone: { mode: "full_surface", shape: "none" },
      namePosition: { x: 5, y: 88 },
      nameSide: "front",
      cardNumberPosition: { x: 5, y: 78 },
      cardNumberSide: "front",
    },
    cardColors: ALL_CARD_COLORS,
    avatarStyles: ALL_AVATAR_STYLES,
    hobbies: ALL_HOBBIES,
    allowedOrientations: ["landscape", "portrait"],
    aiModel: "google/gemini-2.5-flash-image",
    adminEmails: ["cards@acmebank.com", "admin@acmebank.com"],
    officers: [
      { id: "o1", name: "Jane Roberts", email: "jane.roberts@acmebank.com", role: "Card Operations Manager" },
      { id: "o2", name: "Tom Harris", email: "tom.harris@acmebank.com", role: "Compliance Officer" },
    ],
  },
  "nova-finance": {
    id: "t2",
    slug: "nova-finance",
    name: "Nova Finance",
    brandName: "Nova Finance",
    tenantType: "bank",
    primaryColor: "340 75% 55%",
    logoUrl: "",
    freeAttemptsPerUser: 3,
    overagePrice: 1.99,
    billingMode: "user_pays",
    notificationEmail: "cards@novafinance.com",
    landingPage: {
      heroTitle: "Your Card. Your Style. Your Avatar.",
      heroSubtitle: "Nova Finance lets you personalise your card with stunning AI art or your own photo. Make it yours.",
      features: [],
      ctaText: "Get Started",
      footerText: "Powered by Cardmoji · Nova Finance",
    },
    policies: {
      privacyPolicy: `<h2>Privacy Policy — Nova Finance</h2>
<p><strong>Effective Date:</strong> 1 February 2026</p>
<p>Nova Finance ("we", "us", "our") values your privacy. This policy describes our practices regarding personal data collected through our card personalisation platform.</p>
<h3>1. Information We Collect</h3>
<ul><li>Personal identification data (name, account details)</li><li>Uploaded photographs and generated artwork</li><li>Design choices and preferences</li><li>Technical data (IP address, device type)</li></ul>
<h3>2. Purpose of Processing</h3>
<ul><li>Card design and production</li><li>Identity verification</li><li>Service improvement and analytics</li><li>Regulatory compliance</li></ul>
<h3>3. Third-Party Processors</h3>
<p>We use third-party print fulfilment partners. Your data is processed under strict contractual agreements. We do not sell personal data to advertisers.</p>
<h3>4. Data Retention</h3>
<p>Photos are deleted according to the bank's configured retention policy after the print package has been downloaded. AI-generated images are the property of Marketspringpad IP USA LLC (see Terms & Conditions) and are retained as needed. Account data follows our standard banking retention schedule.</p>
<h3>5. Your Rights</h3>
<p>Access, rectification, erasure, restriction, and portability rights apply. Email: dpo@novafinance.com.</p>
<h3>6. Contact</h3>
<p>Nova Finance Data Protection Team: dpo@novafinance.com</p>`,
      termsConditions: `<h2>Terms & Conditions — Nova Finance Card Studio</h2>
<p><strong>Effective Date:</strong> 1 February 2026</p>
<h3>1. About This Service</h3>
<p>Nova Finance Card Studio lets you create a personalised card featuring AI-generated art or your own photograph.</p>
<h3>2. Who Can Use This Service</h3>
<p>Available to Nova Finance customers with an active card product. Users must be 18 or older.</p>
<h3>3. Content Guidelines</h3>
<p>Uploaded content must not be: illegal, harmful, threatening, abusive, defamatory, or infringing on any intellectual property rights. Nova Finance reserves sole discretion to reject submissions.</p>
<h3>4. Generation Limits</h3>
<p>Each user receives a set number of free AI generation attempts. Overages will be charged to your account at the rate displayed before generation.</p>
<h3>5. Card Production</h3>
<p>Approved designs are sent to our print partner for production. Standard delivery is 5–10 business days. Express options may be available.</p>
<h3>6. Intellectual Property</h3>
<p>All AI-generated images and artwork created through this service are the exclusive intellectual property of Marketspringpad IP USA LLC ("IP Owner"). Nova Finance is granted a limited, non-exclusive, non-transferable licence to use the generated images solely for card production and display within its digital banking platforms. You, the cardholder, receive no ownership rights in the generated images and may not reproduce, distribute, or commercially exploit them. Uploaded photographs remain your property.</p>
<h3>7. Limitation of Liability</h3>
<p>Nova Finance liability is limited to the cost of card replacement. We are not liable for indirect or consequential damages.</p>
<h3>8. Jurisdiction</h3>
<p>These terms are governed by the laws of the Republic of Ireland.</p>`,
    },
    allowPhotoOnly: true,
    cardColors: ALL_CARD_COLORS,
    avatarStyles: ALL_AVATAR_STYLES,
    hobbies: ALL_HOBBIES.filter((h) => ["music", "travel", "fitness", "photography"].includes(h.id)),
    allowedOrientations: ["portrait"],
    aiModel: "google/gemini-3-pro-image-preview",
    defaultFulfilment: "webhook",
    printerWebhookUrl: "https://api.printpartner.io/nova/fulfil",
    cardLayout: {
      logoPosition: { x: 5, y: 5 },
      chipPosition: { x: 42, y: 32 },
      avatarZone: {
        mode: "restricted",
        rect: { x: 55, y: 10, width: 40, height: 50 },
        shape: "rounded_rect",
      },
      namePosition: { x: 5, y: 88 },
      nameSide: "front",
      cardNumberPosition: { x: 5, y: 78 },
      cardNumberSide: "front",
    },
    adminEmails: ["cards@novafinance.com"],
    officers: [
      { id: "o3", name: "Priya Patel", email: "priya.patel@novafinance.com", role: "Head of Digital Products" },
    ],
  },
};

// ── Branches ────────────────────────────────────────────────────────
export const MOCK_BRANCHES: Branch[] = [
  { id: "b1", tenantId: "t1", name: "Acme Downtown", address: "123 Main St" },
  { id: "b2", tenantId: "t1", name: "Acme Westside", address: "456 West Ave" },
  { id: "b3", tenantId: "t1", name: "Acme Airport", address: "789 Airport Rd" },
  { id: "b4", tenantId: "t2", name: "Nova Central", address: "10 Central Plaza" },
  { id: "b5", tenantId: "t2", name: "Nova Harbour", address: "22 Harbour Way" },
];

// Legacy exports for backward compat
export const CARD_COLORS = ALL_CARD_COLORS;
export const AVATAR_STYLES = ALL_AVATAR_STYLES;
export const HOBBIES = ALL_HOBBIES;

// ── Mock runtime data ───────────────────────────────────────────────
export const MOCK_AVATAR_VARIANTS: AvatarVariant[] = [
  { id: "av1", jobId: "j1", variantNumber: 1, imageUrl: "/placeholder.svg" },
  { id: "av2", jobId: "j1", variantNumber: 2, imageUrl: "/placeholder.svg" },
];

export const MOCK_ATTEMPT_COUNTER: AttemptCounter = {
  attemptsUsed: 2,
  attemptsFree: 5,
  overageChargedAmount: 0,
};

export const MOCK_CARD_REQUESTS: CardRequest[] = [
  {
    id: "cr1",
    tenantId: "t1",
    userId: "u1",
    cardColor: "ocean",
    orientation: "landscape",
    style: "bitmoji_inspired",
    hobby: "gaming",
    status: "pending",
    firstName: "Alex",
    lastName: "Johnson",
    panLast5: "12345",
    deliveryType: "branch",
    branchId: "b1",
    postalAddress: null,
    selectedAvatarImageId: "av1",
    cardMockUrl: "/placeholder.svg",
    avatarPosition: { x: 50, y: 50 },
    avatarScale: 1,
    createdAt: "2026-02-08T10:00:00Z",
    submittedAt: "2026-02-08T12:00:00Z",
  },
  {
    id: "cr2",
    tenantId: "t1",
    userId: "u2",
    cardColor: "sunset",
    orientation: "portrait",
    style: "pop_art",
    hobby: "cooking",
    status: "pending",
    firstName: "Sara",
    lastName: "Williams",
    panLast5: "67890",
    deliveryType: "postal",
    branchId: null,
    postalAddress: { line1: "42 Oak Lane", city: "Springfield", state: "IL", postalCode: "62701", country: "US" },
    selectedAvatarImageId: "av2",
    cardMockUrl: "/placeholder.svg",
    avatarPosition: { x: 55, y: 40 },
    avatarScale: 1.2,
    createdAt: "2026-02-07T09:00:00Z",
    submittedAt: "2026-02-07T14:00:00Z",
  },
  {
    id: "cr3",
    tenantId: "t1",
    userId: "u3",
    cardColor: "midnight",
    orientation: "landscape",
    style: "superhero_comic",
    hobby: "sports",
    status: "approved",
    firstName: "Mike",
    lastName: "Chen",
    panLast5: "11111",
    deliveryType: "branch",
    branchId: "b2",
    postalAddress: null,
    selectedAvatarImageId: "av1",
    cardMockUrl: "/placeholder.svg",
    avatarPosition: { x: 50, y: 50 },
    avatarScale: 1,
    createdAt: "2026-02-06T08:00:00Z",
    submittedAt: "2026-02-06T11:00:00Z",
    reviewedAt: "2026-02-07T09:30:00Z",
  },
];